import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Button, Tabs, Tab, Table, Form, Spinner, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaSave, FaUpload, FaSync, FaArrowLeft } from 'react-icons/fa';
import api from '../../../api/apiClient';

const initialFormState = {
  version: '',
  title: '',
  content: ''
};

const TermsEditor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('current');
  const [formState, setFormState] = useState(initialFormState);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishingId, setPublishingId] = useState(null);

  const resetForm = useCallback(() => {
    setFormState(initialFormState);
    setEditingDraftId(null);
  }, []);

  const normalizeString = useCallback((value) => {
    if (!value || typeof value !== 'string') return '';
    return value.trim();
  }, []);

  const fetchVersions = useCallback(async ({ showLoader = true } = {}) => {
    try {
      if (showLoader) setLoading(true);

      const response = await api.terms.listVersions();
      if (!response.data?.success) {
        toast.error(response.data?.message || 'Failed to load Terms of Use versions.');
        return;
      }

      const data = response.data.data || {};
      setCurrentVersion(data.currentVersion || null);
      setDrafts(Array.isArray(data.drafts) ? data.drafts : []);
      setHistory(Array.isArray(data.history) ? data.history : []);
    } catch (error) {
      const fallbackMessage = error.response?.data?.message || error.message;
      toast.error(`Failed to load Terms of Use: ${fallbackMessage}`);
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  const handleRefresh = useCallback(async () => {
    if (loading || refreshing) return;
    setRefreshing(true);
    try {
      await fetchVersions({ showLoader: false });
    } finally {
      setRefreshing(false);
    }
  }, [fetchVersions, loading, refreshing]);

  const handleNewDraft = useCallback(() => {
    resetForm();
    setActiveTab('drafts');
  }, [resetForm]);

  const handleEditDraft = useCallback((draft) => {
    if (!draft) return;
    setEditingDraftId(draft.id);
    setFormState({
      version: draft.version || '',
      title: draft.title || '',
      content: draft.content || ''
    });
    setActiveTab('drafts');
  }, []);

  const handleFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormState((previous) => ({
      ...previous,
      [name]: value
    }));
  }, []);

  const validateForm = useCallback(() => {
    const errors = [];
    if (!normalizeString(formState.version)) errors.push('Version is required.');
    if (!normalizeString(formState.title)) errors.push('Title is required.');
    if (!normalizeString(formState.content)) errors.push('Content cannot be empty.');

    if (errors.length) {
      errors.forEach((msg) => toast.error(msg));
      return false;
    }
    return true;
  }, [formState.content, formState.title, formState.version, normalizeString]);

  const handleSaveDraft = useCallback(async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    const payload = {
      version: normalizeString(formState.version),
      title: normalizeString(formState.title),
      content: formState.content,
      status: 'draft'
    };

    setSaving(true);
    try {
      if (editingDraftId) {
        await api.terms.updateVersion(editingDraftId, payload);
        toast.success('Draft updated successfully.');
      } else {
        await api.terms.createVersion(payload);
        toast.success('Draft created successfully.');
      }

      await fetchVersions();
      resetForm();
    } catch (error) {
      const fallbackMessage = error.response?.data?.message || error.message;
      toast.error(`Failed to save draft: ${fallbackMessage}`);
    } finally {
      setSaving(false);
    }
  }, [editingDraftId, fetchVersions, formState.content, formState.title, formState.version, normalizeString, resetForm, validateForm]);

  const handlePublish = useCallback(async (versionId) => {
    if (!versionId) return;
    setPublishingId(versionId);
    try {
      await api.terms.publishVersion(versionId);
      toast.success('Terms of Use version published successfully.');
      await fetchVersions();
      setActiveTab('current');
    } catch (error) {
      const fallbackMessage = error.response?.data?.message || error.message;
      toast.error(`Failed to publish version: ${fallbackMessage}`);
    } finally {
      setPublishingId(null);
    }
  }, [fetchVersions]);

  const sortedDrafts = useMemo(() => {
    return [...drafts].sort((a, b) => {
      const aDate = new Date(a.createdAt || 0).getTime();
      const bDate = new Date(b.createdAt || 0).getTime();
      return bDate - aDate;
    });
  }, [drafts]);

  const sortedHistory = useMemo(() => {
    return [...history].sort((a, b) => {
      const aDate = new Date(a.published_at || a.createdAt || 0).getTime();
      const bDate = new Date(b.published_at || b.createdAt || 0).getTime();
      return bDate - aDate;
    });
  }, [history]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-3 sm:p-6 lg:p-8">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-3">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/site-management')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium"
          >
            <FaArrowLeft className="w-4 h-4" /> Back
          </button>
          <div>
            <h1 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
              Terms of Use
            </h1>
            <p className="text-gray-600 mt-1">
              Manage drafts, publish versions, and track history of your legal terms
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleRefresh}
            disabled={loading || refreshing}
            className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 px-8 py-2"
          >
            {refreshing ? (
              <>
                <Spinner animation="border" size="sm" />
                Refreshing...
              </>
            ) : (
              <>
                <FaSync className="w-5 h-5" />
                Refresh
              </>
            )}
          </Button>
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleNewDraft}
            className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 px-8 py-2"
          >
            <FaPlus className="w-5 h-5" />
            New Draft
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-2xl rounded-3xl overflow-hidden bg-white/90 backdrop-blur-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <Spinner animation="border" className="w-12 h-12 text-purple-600 mb-4" />
                <p className="text-lg text-gray-600">Loading Terms of Use...</p>
              </div>
            </div>
          ) : (
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => k && setActiveTab(k)}
              className="border-b-0 bg-gradient-to-r from-purple-200 to-pink-200"
              fill
            >
              {/* Current Version Tab */}
              <Tab eventKey="current" title={<span className="text-grey-600 font-semibold px-4">Current Version</span>}>
                <div className="p-8">
                  {currentVersion ? (
                    <div className="space-y-8">
                      <div>
                        <h2 className="text-3xl font-bold text-gray-900">
                          {currentVersion.title || 'Untitled Terms of Use'}
                        </h2>
                        <div className="flex items-center gap-4 mt-3">
                          <span className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-full text-sm">
                            Version {currentVersion.version || '-'}
                          </span>
                          <Badge bg="emerald-100" text="emerald-800" className="px-4 py-2 text-sm font-bold">
                            {currentVersion.status || 'Active'}
                          </Badge>
                        </div>
                        <p className="text-gray-600 mt-3">
                          Published on{' '}
                          <span className="font-semibold">
                            {currentVersion.published_at
                              ? new Date(currentVersion.published_at).toLocaleString()
                              : 'N/A'}
                          </span>
                        </p>
                      </div>
                      <div className="bg-gray-50 border-2 border-dashed border-purple-200 rounded-3xl p-8 max-h-96 overflow-y-auto font-medium text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {currentVersion.content || 'No content available for this version.'}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-20">
                      <div className="text-6xl mb-6 text-gray-300">Document</div>
                      <p className="text-xl text-gray-600 font-medium">
                        No published Terms of Use yet.
                      </p>
                      <p className="text-gray-500 mt-3">
                        Create a draft and publish it to make it the active version.
                      </p>
                    </div>
                  )}
                </div>
              </Tab>

              {/* Drafts Tab */}
              <Tab eventKey="drafts" title={<span className="text-grey-600 font-semibold px-4">Drafts</span>}>
                <div className="p-8">
                  <div className="grid lg:grid-cols-2 gap-10">
                    {/* Draft List */}
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">Draft Versions</h3>
                      {sortedDrafts.length === 0 ? (
                        <div className="bg-gray-50 border-2 border-dashed border-purple-200 rounded-3xl p-12 text-center">
                          <p className="text-gray-600">No drafts found.</p>
                          <p className="text-sm text-gray-500 mt-2">Click "New Draft" to create one!</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {sortedDrafts.map((draft) => (
                            <div key={draft.id} className="bg-white border border-purple-100 rounded-2xl p-6 hover:shadow-xl hover:border-purple-300 transition-all duration-300">
                              <div className="flex justify-between items-start">
                                <div>
                                  <h4 className="font-bold text-lg text-gray-900">
                                    Version {draft.version || 'Untitled'}
                                  </h4>
                                  <p className="text-sm text-gray-600 mt-1">
                                    Created {draft.createdAt ? new Date(draft.createdAt).toLocaleDateString() : 'N/A'}
                                  </p>
                                </div>
                                <div className="flex gap-3">
                                  <Button
                                    bsPrefix="custom-hamburger"
                                    onClick={() => handleEditDraft(draft)}
                                    className="border-purple-300 text-purple-600 hover:bg-purple-100 rounded-lg px-4 py-2 font-semibold"
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    bsPrefix="custom-hamburger"
                                    onClick={() => handlePublish(draft.id)}
                                    disabled={publishingId === draft.id}
                                    className="border-emerald-500 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg px-4 py-2 font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
                                  >
                                    {publishingId === draft.id ? (
                                      <>
                                        <Spinner animation="border" size="sm" />
                                        Publishing
                                      </>
                                    ) : (
                                      <>
                                        <FaUpload className="w-4 h-4" />
                                        Publish
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Draft Editor */}
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        {editingDraftId ? 'Edit Draft' : 'Create New Draft'}
                      </h3>
                      <Form onSubmit={handleSaveDraft} className="space-y-6">
                        <div className="grid sm:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Version</label>
                            <input
                              type="text"
                              name="version"
                              value={formState.version}
                              onChange={handleFormChange}
                              placeholder="e.g. 2.1"
                              disabled={saving}
                              className="w-full px-5 py-3.5 rounded-2xl border border-purple-200 focus:outline-none focus:ring-4 focus:ring-purple-500/30 bg-white shadow-sm transition-all font-medium"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Title</label>
                            <input
                              type="text"
                              name="title"
                              value={formState.title}
                              onChange={handleFormChange}
                              placeholder="Terms of Use - Updated"
                              disabled={saving}
                              className="w-full px-5 py-3.5 rounded-2xl border border-purple-200 focus:outline-none focus:ring-4 focus:ring-purple-500/30 bg-white shadow-sm transition-all font-medium"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-2">Content (English)</label>
                          <textarea
                            rows={16}
                            name="content"
                            value={formState.content}
                            onChange={handleFormChange}
                            placeholder="Write your Terms of Use content here..."
                            disabled={saving}
                            className="w-full px-6 py-5 rounded-2xl border border-purple-200 focus:outline-none focus:ring-4 focus:ring-purple-500/30 bg-white shadow-sm transition-all font-medium text-gray-700 resize-none"
                          />
                          <p className="text-sm text-gray-500 mt-3">
                            Tip: This will be upgraded to a rich text editor (TipTap) soon!
                          </p>
                        </div>

                        <div className="flex justify-end">
                          <Button
                            bsPrefix="custom-hamburger"
                            type="submit"
                            disabled={saving}
                            className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm px-8 py-2 flex items-center gap-3"
                          >
                            {saving ? (
                              <>
                                <Spinner animation="border" size="sm" />
                                Saving Draft...
                              </>
                            ) : (
                              <>
                                <FaSave className="w-5 h-5" />
                                Save Draft
                              </>
                            )}
                          </Button>
                        </div>
                      </Form>
                    </div>
                  </div>
                </div>
              </Tab>

              {/* History Tab */}
              <Tab eventKey="history" title={<span className="text-grey-600 font-semibold px-4">Version History</span>}>
                <div className="p-8">
                  {sortedHistory.length === 0 ? (
                    <div className="text-center py-20 bg-gray-50 rounded-3xl">
                      <p className="text-xl text-gray-600">No published history yet.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto rounded-3xl border border-purple-100">
                      <Table hover className="min-w-full">
                        <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                          <tr>
                            <th className="px-6 py-5 text-left font-bold">Version</th>
                            <th className="px-6 py-5 text-left font-bold">Title</th>
                            <th className="px-6 py-5 text-left font-bold">Published</th>
                            <th className="px-6 py-5 text-center font-bold">Status</th>
                            <th className="px-6 py-5 text-left font-bold">Notes</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-purple-100">
                          {sortedHistory.map((item) => (
                            <tr key={item.id} className="hover:bg-purple-50/50 transition-all">
                              <td className="px-6 py-5 font-bold text-purple-700">{item.version || '-'}</td>
                              <td className="px-6 py-5 font-medium text-gray-900">{item.title || '-'}</td>
                              <td className="px-6 py-5 text-gray-600">
                                {item.published_at ? new Date(item.published_at).toLocaleString() : 'N/A'}
                              </td>
                              <td className="px-6 py-5 text-center">
                                <span className={`inline-block px-4 py-2 rounded-full text-sm font-bold ${
                                  item.status === 'active' 
                                    ? 'bg-emerald-100 text-emerald-800' 
                                    : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {item.status || 'Archived'}
                                </span>
                              </td>
                              <td className="px-6 py-5 text-sm text-gray-600">{item.changelog || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </Tab>
            </Tabs>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default TermsEditor;